import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/cn';

interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (query: string) => void;
  options: string[];
  placeholder?: string;
  /** Разрешить свободный ввод значения, которого нет в справочнике. */
  allowCustom?: boolean;
  disabled?: boolean;
  className?: string;
  /** Уровень портала для combobox внутри вложенных модальных окон. */
  panelClassName?: string;
  id?: string;
  'aria-label'?: string;
}

type PanelPosition = {
  left: number;
  top?: number;
  bottom?: number;
  width: number;
  maxHeight: number;
};

/**
 * Combobox — выпадающий список с поиском по вводу. Для длинных справочников
 * (категории, поводы, сезоны), где обычный Select неудобен. Стилистически
 * повторяет DS-компонент Select, значения фильтруются по мере набора.
 */
export function Combobox({
  value,
  onChange,
  onSearch,
  options,
  placeholder = 'Выберите или начните вводить',
  allowCustom = false,
  disabled,
  className,
  panelClassName,
  id,
  'aria-label': ariaLabel,
}: ComboboxProps) {
  const reactId = useId();
  const inputId = id ?? `combobox-${reactId}`;
  const listboxId = `${inputId}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [position, setPosition] = useState<PanelPosition | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  // В инпуте показываем набранный запрос, пока открыто; иначе — выбранное значение.
  const displayValue = open ? query : value;

  const updatePosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(Math.max(rect.width, 200), viewportWidth - 16);
    const left = Math.min(Math.max(8, rect.left), Math.max(8, viewportWidth - width - 8));
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const placeAbove = spaceBelow < 200 && spaceAbove > spaceBelow;
    const available = Math.max(120, placeAbove ? spaceAbove - 4 : spaceBelow - 4);
    setPosition({
      left,
      width,
      maxHeight: Math.min(288, available),
      ...(placeAbove ? { bottom: viewportHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handler = () => updatePosition();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (inputRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      commitAndClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, allowCustom]);

  const openMenu = () => {
    if (disabled) return;
    setQuery('');
    setHighlighted(0);
    setOpen(true);
  };

  const choose = (val: string) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  /** Закрытие без явного выбора: при allowCustom фиксируем набранный текст. */
  const commitAndClose = () => {
    if (allowCustom && query.trim()) {
      onChange(query.trim());
    }
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Tab') {
      // Уход с поля клавиатурой фиксирует набранное — как и уход мышью, который
      // ловит обработчик mousedown выше. Без этой ветки Tab оставлял значение
      // жить только в локальном query: человек видел свой текст в поле, а
      // наверх уходило прежнее значение, и форма молча считала по-старому.
      // Фокус не перехватываем: preventDefault здесь сломал бы обход табом.
      if (open) commitAndClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) openMenu();
      else setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open && filtered[highlighted]) choose(filtered[highlighted]);
      else if (allowCustom && query.trim()) choose(query.trim());
      else setOpen(false);
    } else if (event.key === 'Escape') {
      // Закрытый список Esc не трогает: его получит окно вокруг. Открытый — закрывается и гасит событие.
      if (!open) return;
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  const panel =
    open && position
      ? createPortal(
          <div
            ref={panelRef}
            id={listboxId}
            role="listbox"
            className={cn(
              'fixed z-[80] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg',
              'dark:border-zinc-800 dark:bg-zinc-900',
              panelClassName,
            )}
            style={{
              left: position.left,
              top: position.top,
              bottom: position.bottom,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
          >
            {filtered.length === 0 ? (
              allowCustom && query.trim() ? (
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  // mousedown вместо click: документный обработчик ниже слушает
                  // mousedown и закрывает список раньше, чем дошёл бы click —
                  // без preventDefault клик по этому пункту не долетал бы,
                  // потому что фиксация выбора должна опередить закрытие.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(query.trim());
                  }}
                  className="flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800/70"
                >
                  <span className="min-w-0 flex-1 whitespace-normal break-words">
                    {`Добавить «${query.trim()}»`}
                  </span>
                </button>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-400 dark:text-zinc-500">Ничего не найдено</div>
              )
            ) : (
              filtered.map((option, index) => {
                const selected = option === value;
                const isHighlighted = index === highlighted;
                return (
                  <button
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => choose(option)}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                      selected
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300'
                        : 'text-gray-700 dark:text-zinc-200',
                      isHighlighted && !selected && 'bg-gray-50 dark:bg-zinc-800/70',
                    )}
                  >
                    <span className="min-w-0 flex-1 whitespace-normal break-words">{option}</span>
                    {selected && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={cn('relative', className)}>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        disabled={disabled}
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          onSearch?.(e.target.value);
          setHighlighted(0);
          if (!open) setOpen(true);
        }}
        onFocus={openMenu}
        onClick={openMenu}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-14 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors',
          'focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500',
          'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400',
          'dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder-zinc-500 dark:disabled:bg-zinc-800',
        )}
      />
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
        {value && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => {
              e.preventDefault();
              onChange('');
              setQuery('');
            }}
            className="pointer-events-auto rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-zinc-200"
            aria-label="Очистить"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-gray-400 transition-transform dark:text-zinc-500', open && 'rotate-180')}
        />
      </div>
      {panel}
    </div>
  );
}

export default Combobox;
